import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevron } from '../../assets/icons';
import { InputMui } from '../input-mui/input-mui';
import { DialogCheckboxes } from './dialog-checkboxes';

export type CheckboxesOptions<T> = {
  label: string;
  value: T;
  checked: boolean;
  disabled?: boolean;
  data?: string;
};

type CheckboxSelectProps<T> = {
  options: CheckboxesOptions<T>[];
  multipleLabel: string;
  onSave: (options: CheckboxesOptions<T>[]) => void;
  disabled?: boolean;
  reverse?: boolean;
  label?: string;
  showDialogLabel?: boolean;
};

export const CheckboxSelect = <T extends string | number | readonly string[] | undefined>({
  options,
  disabled = false,
  onSave,
  reverse,
  multipleLabel,
  label,
  showDialogLabel = false,
}: CheckboxSelectProps<T>) => {
  const { t } = useTranslation('common');
  const [tempOptions, setTempOptions] = useState<CheckboxesOptions<T>[]>([]);
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);

  const onClick = () => {
    setTempOptions([...options]);
    setShowSelectDialog(true);
  };

  const inputTitle = useMemo(() => {
    const checkedOptions = options.filter((x) => x.checked && !x.disabled);
    if (!checkedOptions.length) return t('select');

    if (checkedOptions.length === 1) return checkedOptions[0].label;
    return `${multipleLabel} (${checkedOptions.length})`;
  }, [options, multipleLabel]);

  const handleOnChange = (value: T) => {
    setTempOptions((prev) => {
      const temp = [...prev];
      const index = temp.findIndex((x) => x.value === value);

      if (index !== -1) {
        temp[index].checked = !temp[index].checked;
      }

      return [...temp];
    });
  };

  const handleSave = () => {
    onSave(tempOptions);
    setShowSelectDialog(false);
  };

  return (
    <div className="checkbox-select">
      <InputMui
        value={inputTitle}
        label={label}
        readOnly
        disabled={disabled}
        endIcon={<IconChevron direction="right" noSpace />}
        onClickInput={onClick}
        reverse={reverse}
      />
      <DialogCheckboxes
        show={showSelectDialog && !disabled}
        setShow={setShowSelectDialog}
        options={tempOptions}
        onChange={handleOnChange}
        handleSave={handleSave}
        {...(showDialogLabel ? { label } : {})}
      />
    </div>
  );
};
